import React, {useMemo} from 'react';
import {Input, Select, Typography} from 'antd';
import {useQuery} from '@apollo/client';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {
  GET_WMS_POD_EXEMPT_REPORTS,
  GET_WMS_POD_EXEMPT_REVIEW_STATS,
  WmsPodExemptReportsResponse,
  WmsPodExemptReviewStatsResponse,
} from '../../../graphql/podExempt/queries';
import {PodExemptReportFiltersInput, PodExemptStatus} from '../../../graphql/podExempt/types';
import PodExemptReportTable from '../../../components/podExempt/PodExemptReportTable';
import PodExemptReviewQueueStats from '../../../components/podExempt/PodExemptReviewQueueStats';
import TablePaginationBar from '../../../components/TablePaginationBar';
import {usePodExemptDict} from '../../../hooks/usePodExemptDict';
import {useWarehouseContext} from '../../../contexts/WarehouseContext';

/** 仓管后台：门牌免拍初审队列（点「审核」进入审核页；数据域由服务端注入）。 */
const WmsPodExemptListPage: React.FC = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  // 原型队列无状态筛选：默认展示全部状态（各状态在状态列区分），待审优先由后端排序保证
  // 顶部仓库下拉：列表按当前选中仓收窄（服务端仍与账号可见仓求交，前端传值不被采信）
  const {currentWarehouse} = useWarehouseContext();
  const warehouseId = currentWarehouse?.id ?? null;
  /**
   * 筛选与分页存 URL query，而不是组件 state。
   *
   * 从列表点进审核页会卸载本组件，state 随之丢失 —— 审核完返回列表，原先选好的
   * 「待初审」和页码全被重置，得重新选一遍。放进 URL 后：返回列表带着 query 回来即可复原，
   * 刷新不丢，筛好的列表链接也能直接发给别人。
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = (searchParams.get('status') ?? '') as PodExemptStatus | '';
  const searchTextParam = searchParams.get('q') ?? '';
  const page = Number(searchParams.get('page')) || 1;
  const pageSize = Number(searchParams.get('size')) || 10;
  const filters: PodExemptReportFiltersInput = useMemo(
    () => ({
      statuses: statusParam ? [statusParam] : null,
      searchText: searchTextParam || null,
    }),
    [statusParam, searchTextParam],
  );
  /** 改筛选/翻页统一走这里；传 null 即删除该参数（回到默认值），replace 避免每次筛选都堆一条历史 */
  const patchParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setSearchParams(next, {replace: true});
  };

  // warehouseId 后端必填（资源码 + 仓权限双重鉴权），仓上下文未就绪时不发请求
  const {data, loading} = useQuery<WmsPodExemptReportsResponse>(GET_WMS_POD_EXEMPT_REPORTS, {
    variables: {filters: {...filters, warehouseId: warehouseId!}, pageRequest: {page, size: pageSize}},
    skip: warehouseId == null,
    fetchPolicy: 'cache-and-network',
  });
  const {data: statsData, refetch: refetchStats} = useQuery<WmsPodExemptReviewStatsResponse>(
    GET_WMS_POD_EXEMPT_REVIEW_STATS,
    {variables: {warehouseId: warehouseId!}, skip: warehouseId == null, fetchPolicy: 'cache-and-network'},
  );

  const {items: reasonDictAll} = usePodExemptDict('REASON');
  const {items: addressTypeDictAll} = usePodExemptDict('ADDRESS_TYPE');
  const dictNameOf = useMemo(() => {
    const map = new Map<string, string>();
    [...reasonDictAll, ...addressTypeDictAll].forEach((item) => map.set(item.code, item.name));
    return (code: string) => map.get(code) ?? code;
  }, [reasonDictAll, addressTypeDictAll]);

  const pageData = data?.wmsPodExemptReports;

  const openReview = (reportId: string) =>
    navigate(`/wms/pod-exempt/review/${reportId}?${searchParams}`);

  return (
    <div className="p-4">
      <Typography.Title level={4}>{t('podExempt.menu.wms')}</Typography.Title>
      {/* 原型布局：统计卡行右侧放单框搜索（底部对齐，无状态筛选） */}
      {/* flex-wrap 必需：统计卡约 804px + 筛选区 456px（w-32 + w-80）+ 间距 ≈ 1308px，再加左侧菜单约 340px，
          不换行需要约 1650px 视口才不溢出 —— 窄屏下搜索框与「筛选」按钮会被推出可视区 */}
      <div className="flex items-end justify-between gap-4 mb-3 flex-wrap">
        <PodExemptReviewQueueStats
          stats={statsData?.wmsPodExemptReviewStats ?? null}
          overdueLabel={t('podExempt.review.statOver12h')}
          passRateLabel={t('podExempt.review.statWeeklyPassRate')}
          pendingLabel={t('podExempt.status.pendingFirst')}
        />
        <div className="flex gap-2 flex-none mb-1">
          <Select<PodExemptStatus | ''>
            className="w-32"
            value={statusParam}
            onChange={(value) => {
              patchParams({status: value || null, page: null});
              refetchStats();
            }}
            options={[
              {value: '', label: t('podExempt.filter.allStatus')},
              {value: 'PENDING_FIRST', label: t('podExempt.status.pendingFirst')},
              {value: 'PENDING_FINAL', label: t('podExempt.status.pendingFinal')},
              {value: 'EFFECTIVE', label: t('podExempt.status.effective')},
              {value: 'REJECTED', label: t('podExempt.status.rejected')},
              {value: 'CLOSED', label: t('podExempt.status.closed')},
              {value: 'OFFLINE', label: t('podExempt.status.offline')},
            ]}
          />
          <Input.Search
            className="w-80"
            allowClear
            enterButton={t('podExempt.filter.search')}
            placeholder={t('podExempt.review.searchPlaceholder') ?? ''}
            onSearch={(value) => {
              patchParams({q: value.trim() || null, page: null});
              // 统计卡与列表一体刷新（统计不带筛选参数，需手动触发）
              refetchStats();
            }}
          />
        </div>
      </div>
      <PodExemptReportTable
        variant="review"
        data={pageData?.content ?? []}
        loading={loading}
        dictNameOf={dictNameOf}
        onRowClick={(record) => openReview(record.id)}
        renderActions={(record) => (
          <Typography.Link onClick={() => openReview(record.id)}>
            {record.status === 'PENDING_FIRST'
              ? t('podExempt.review.reviewAction')
              : t('podExempt.common.detail')}
          </Typography.Link>
        )}
      />
      <TablePaginationBar
        total={Number(pageData?.totalElements ?? 0)}
        current={page}
        pageSize={pageSize}
        onChange={(nextPage, nextSize) => {
          patchParams({
            page: nextPage > 1 ? String(nextPage) : null,
            size: nextSize !== 10 ? String(nextSize) : null,
          });
          refetchStats();
        }}
      />
    </div>
  );
};

export default WmsPodExemptListPage;
